import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { fetchByIds, fetchOrgMembers } from "@/lib/large-org";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  ArrowLeft, MessageSquare, AlertTriangle, UserPlus, ArrowRightLeft,
  Clock, MapPin, Send, Trash2, Flame, ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import {
  INCIDENT_SEVERITIES, INCIDENT_STATUSES, INCIDENT_CATEGORIES,
  SEVERITY_STYLES, STATUS_STYLES,
} from "@/lib/incident-types";
import { IncidentAttachments } from "@/components/IncidentAttachments";
import { IncidentFollowups } from "@/components/IncidentFollowups";
import { IncidentRevisions } from "@/components/IncidentRevisions";
import { useEscalateIncident } from "@/lib/incidents";

export const Route = createFileRoute("/_authenticated/incidents/$incidentId/")({
  component: IncidentDetail,
  head: () => ({ meta: [{ title: "Incident — MissionReady" }] }),
});

type Incident = {
  id: string; organization_id: string; reference_number: string;
  category: string; subtype: string | null; severity: string; status: string;
  title: string | null; narrative: string | null; location_description: string | null;
  people_involved: string | null; witness_names: string | null;
  occurrence_at: string; created_at: string; updated_at: string;
  reporter_id: string; assignee_id: string | null; site_id: string | null;
  escalated?: boolean; escalated_at?: string | null;
};

type Member = { user_id: string; role: string; profile?: { full_name: string | null; email: string | null } };
type Event = { id: number; event_type: string; message: string | null; metadata: any; actor_id: string | null; created_at: string };

const EVENT_ICON: Record<string, any> = {
  created: AlertTriangle, status_changed: ArrowRightLeft, closed: ArrowRightLeft, reopened: ArrowRightLeft,
  assigned: UserPlus, unassigned: UserPlus, severity_changed: AlertTriangle,
  commented: MessageSquare, attachment_added: MessageSquare, updated: ArrowRightLeft,
};

function IncidentDetail() {
  const { incidentId } = Route.useParams();
  const { currentOrg, currentRole, user } = useAuth();
  const isStaff = ["org_owner", "admin", "supervisor", "dispatcher"].includes(currentRole ?? "");
  const isAdmin = ["org_owner", "admin"].includes(currentRole ?? "");
  const escalate = useEscalateIncident(incidentId);
  const [incident, setIncident] = useState<Incident | null>(null);
  const [siteName, setSiteName] = useState("");
  const [members, setMembers] = useState<Member[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [comment, setComment] = useState("");
  const [profilesById, setProfilesById] = useState<Record<string, { full_name: string | null; email: string | null }>>({});

  const load = async () => {
    if (!currentOrg) return;
    const { data: inc } = await supabase.from("incidents").select("*").eq("id", incidentId).single();
    setIncident(inc as any);
    if (inc?.site_id) {
      const { data: s } = await supabase.from("sites").select("name").eq("id", inc.site_id).single();
      setSiteName(s?.name ?? "");
    }
    const [ms, { data: ev }] = await Promise.all([
      fetchOrgMembers<{ user_id: string; role: string }>(currentOrg.id, "user_id, role"),
      supabase.from("incident_timeline").select("*").eq("incident_id", incidentId).order("created_at", { ascending: true }),
    ]);
    setMembers((ms as any) ?? []);
    setEvents((ev as any) ?? []);

    const ids = Array.from(new Set([
      ...((ms as any[]) ?? []).map((m) => m.user_id),
      ...((ev as any[]) ?? []).map((e) => e.actor_id).filter(Boolean),
      inc?.reporter_id, inc?.assignee_id,
    ].filter(Boolean) as string[]));
    if (ids.length) {
      const profs = await fetchByIds<any>("profiles", "id, full_name, email", ids);
      const map: Record<string, any> = {};
      for (const p of (profs as any[]) ?? []) map[p.id] = { full_name: p.full_name, email: p.email };
      setProfilesById(map);
    }
  };

  useEffect(() => { load(); }, [incidentId, currentOrg?.id]);

  // realtime on this incident's timeline
  useEffect(() => {
    if (!currentOrg) return;
    const ch = supabase.channel(`incident-${incidentId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "incident_timeline", filter: `incident_id=eq.${incidentId}` },
        () => load())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "incidents", filter: `id=eq.${incidentId}` },
        () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [incidentId, currentOrg?.id]);

  const update = async (patch: Partial<Incident>) => {
    const { error } = await supabase.from("incidents").update(patch).eq("id", incidentId);
    if (error) { toast.error(error.message); return; }
    load();
  };

  const addComment = async () => {
    if (!comment.trim() || !currentOrg || !user) return;
    const text = comment.trim();
    setComment("");
    const { error } = await supabase.from("incident_timeline").insert({
      incident_id: incidentId,
      organization_id: currentOrg.id,
      actor_id: user.id,
      event_type: "commented",
      message: text,
    });
    if (error) toast.error(error.message);
  };

  const remove = async () => {
    if (!confirm("Delete this incident permanently?")) return;
    const { error } = await supabase.from("incidents").delete().eq("id", incidentId);
    if (error) { toast.error(error.message); return; }
    toast.success("Incident deleted");
    window.location.href = "/incidents";
  };

  const userLabel = (id: string | null | undefined) =>
    id ? (profilesById[id]?.full_name || profilesById[id]?.email || id.slice(0, 8)) : "Unassigned";

  if (!incident) {
    return <div className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2"><Link to="/incidents"><ArrowLeft className="mr-1 h-4 w-4" /> All incidents</Link></Button>

      {/* Header */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{incident.reference_number}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${SEVERITY_STYLES[incident.severity]}`}>{incident.severity}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_STYLES[incident.status]}`}>{incident.status.replace("_", " ")}</span>
            </div>
            <h1 className="mt-2 font-display text-2xl font-bold md:text-3xl">
              {incident.title || incident.subtype || INCIDENT_CATEGORIES.find((c) => c.value === incident.category)?.label}
            </h1>
            <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{siteName || "Unassigned site"}</span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />Occurred {new Date(incident.occurrence_at).toLocaleString()}</span>
              <span>Reported by {userLabel(incident.reporter_id)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isStaff && (
              <Button asChild variant="outline" size="sm">
                <Link to="/incidents/$incidentId/follow-up" params={{ incidentId }}>
                  <ListChecks className="mr-1 h-4 w-4" /> Follow-up
                </Link>
              </Button>
            )}
            {isStaff && (
              <Button
                onClick={() => escalate.mutate(!incident.escalated)}
                variant={incident.escalated ? "destructive" : "outline"}
                size="sm"
              >
                <Flame className="mr-1 h-4 w-4" /> {incident.escalated ? "De-escalate" : "Escalate"}
              </Button>
            )}
            {isAdmin && (
              <Button onClick={remove} variant="ghost" size="sm" className="text-destructive">
                <Trash2 className="mr-1 h-4 w-4" /> Delete
              </Button>
            )}
          </div>
        </div>
        {incident.escalated && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
            <Flame className="h-3.5 w-3.5" />
            Escalated{incident.escalated_at ? ` · ${new Date(incident.escalated_at).toLocaleString()}` : ""}
          </div>
        )}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {/* Main */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-semibold">Narrative</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm">{incident.narrative || <span className="text-muted-foreground">No narrative recorded.</span>}</p>

            {(incident.location_description || incident.people_involved || incident.witness_names) && (
              <div className="mt-4 grid gap-3 border-t border-border pt-4 sm:grid-cols-3">
                {incident.location_description && (
                  <div><div className="text-xs uppercase text-muted-foreground">Location</div><div className="mt-1 text-sm">{incident.location_description}</div></div>
                )}
                {incident.people_involved && (
                  <div><div className="text-xs uppercase text-muted-foreground">People involved</div><div className="mt-1 whitespace-pre-wrap text-sm">{incident.people_involved}</div></div>
                )}
                {incident.witness_names && (
                  <div><div className="text-xs uppercase text-muted-foreground">Witnesses</div><div className="mt-1 whitespace-pre-wrap text-sm">{incident.witness_names}</div></div>
                )}
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="rounded-xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-semibold">Timeline</h2>
            <ol className="mt-4 space-y-4">
              {events.map((e) => {
                const Icon = EVENT_ICON[e.event_type] ?? AlertTriangle;
                return (
                  <li key={e.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="mt-1 w-px flex-1 bg-border" />
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="text-sm">
                        <span className="font-medium">{userLabel(e.actor_id)}</span>{" "}
                        <span className="text-muted-foreground">·</span>{" "}
                        <span className="capitalize">{e.event_type.replace("_", " ")}</span>
                      </div>
                      {e.message && (
                        <div className={`mt-1 text-sm ${e.event_type === "commented" ? "rounded-md border border-border bg-muted/30 p-3" : "text-muted-foreground"}`}>
                          {e.message}
                        </div>
                      )}
                      <div className="mt-1 text-[11px] text-muted-foreground">{new Date(e.created_at).toLocaleString()}</div>
                    </div>
                  </li>
                );
              })}
            </ol>

            {/* Comment box */}
            <div className="mt-4 rounded-md border border-border bg-background/50 p-3">
              <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment to the timeline…" />
              <div className="mt-2 flex justify-end">
                <Button size="sm" onClick={addComment} disabled={!comment.trim()}>
                  <Send className="mr-1 h-4 w-4" /> Post comment
                </Button>
              </div>
            </div>
          </div>

          {currentOrg && user && (
            <IncidentAttachments
              incidentId={incidentId}
              orgId={currentOrg.id}
              userId={user.id}
              canDelete={isStaff}
            />
          )}

          {user && (
            <IncidentFollowups
              incidentId={incidentId}
              userId={user.id}
              canResolve={isStaff}
            />
          )}

          <IncidentRevisions incidentId={incidentId} profilesById={profilesById} canRevert={isStaff} />
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-display text-base font-semibold">Status & assignment</h3>

            <div className="mt-4 grid gap-1.5">
              <label className="text-xs text-muted-foreground">Status</label>
              <Select value={incident.status} onValueChange={(v) => update({ status: v })} disabled={!isStaff && incident.reporter_id !== user?.id}>
                <SelectTrigger aria-label="Status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INCIDENT_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_"," ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-3 grid gap-1.5">
              <label className="text-xs text-muted-foreground">Severity</label>
              <Select value={incident.severity} onValueChange={(v) => update({ severity: v })} disabled={!isStaff && incident.reporter_id !== user?.id}>
                <SelectTrigger aria-label="Severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INCIDENT_SEVERITIES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-3 grid gap-1.5">
              <label className="text-xs text-muted-foreground">Assignee</label>
              <Select
                value={incident.assignee_id ?? "__unassigned"}
                onValueChange={(v) => update({ assignee_id: v === "__unassigned" ? null : v })}
                disabled={!isStaff}
              >
                <SelectTrigger aria-label="Assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__unassigned">Unassigned</SelectItem>
                  {members.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {profilesById[m.user_id]?.full_name || profilesById[m.user_id]?.email || m.user_id.slice(0, 8)} · {m.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 text-sm">
            <h3 className="font-display text-base font-semibold">Details</h3>
            <dl className="mt-3 space-y-1.5">
              <div className="flex justify-between"><dt className="text-muted-foreground">Reference</dt><dd className="font-mono text-xs">{incident.reference_number}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Category</dt><dd>{INCIDENT_CATEGORIES.find((c) => c.value === incident.category)?.label}</dd></div>
              {incident.subtype && <div className="flex justify-between"><dt className="text-muted-foreground">Type</dt><dd className="text-right">{incident.subtype}</dd></div>}
              <div className="flex justify-between"><dt className="text-muted-foreground">Reporter</dt><dd>{userLabel(incident.reporter_id)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Assignee</dt><dd>{userLabel(incident.assignee_id)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Created</dt><dd>{new Date(incident.created_at).toLocaleString()}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Updated</dt><dd>{new Date(incident.updated_at).toLocaleString()}</dd></div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
